/**
 * Resolving a scanned (or typed) tub code to a store tub.
 *
 * Jack chose scan-every-tub (2026-09-11): there is no "take 19" shortcut, every
 * tub that goes on a trolley is individually sighted. A real HID scanner makes
 * that free, but a label can be scuffed and the sandbox has no printer, so the
 * resolver also accepts any UNIQUE SUFFIX of a tub code — "1-05", or "05" when
 * only one candidate ends that way. Ambiguity is never guessed: two matches is
 * an error naming both, which is the only safe answer when the operator is
 * holding a physical tub.
 */
import { InkUnitRow } from '../productionApi';

export type ScanOutcome =
  | { kind: 'ok'; unit: InkUnitRow }
  | { kind: 'duplicate'; unit: InkUnitRow }
  | { kind: 'ambiguous'; candidates: string[] }
  | { kind: 'unknown' };

/**
 * @param raw        what the scanner or keyboard produced
 * @param units      every sealed tub at the source location, by unit_code
 * @param alreadyOn  codes already on this trolley load
 */
export function resolveScan(
  raw: string,
  units: Map<string, InkUnitRow>,
  alreadyOn: ReadonlySet<string>,
): ScanOutcome {
  const code = raw.trim().toUpperCase();
  if (!code) return { kind: 'unknown' };

  const exact = units.get(code);
  if (exact) {
    return alreadyOn.has(exact.unit_code)
      ? { kind: 'duplicate', unit: exact }
      : { kind: 'ok', unit: exact };
  }

  // Suffix fallback. Anchored to the END so a short code can never match the
  // middle of a longer one, and so the tub number (the bit that differs within
  // a batch) is what a human would naturally type.
  const matches: InkUnitRow[] = [];
  units.forEach((u) => { if (u.unit_code.endsWith(code)) matches.push(u); });
  if (matches.length === 0) return { kind: 'unknown' };
  if (matches.length > 1) {
    return { kind: 'ambiguous', candidates: matches.map((m) => m.unit_code).sort().slice(0, 6) };
  }
  const only = matches[0];
  return alreadyOn.has(only.unit_code)
    ? { kind: 'duplicate', unit: only }
    : { kind: 'ok', unit: only };
}
